/* global axios */
import ApiClient from './ApiClient';

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    bot_type,
  }) {
    return axios.get(`${this.url}`, {
      params: {
        metric,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        bot_type,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getDrilldown({
    metric,
    bucketTimestamp,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    bot_type,
    page,
    perPage,
    signal,
  }) {
    const requestConfig = {
      params: {
        metric,
        bucket_timestamp: bucketTimestamp,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        bot_type,
        timezone_offset: getTimeOffset(),
        page,
        per_page: perPage,
      },
    };

    if (signal) {
      requestConfig.signal = signal;
    }

    return axios.get(`${this.url}/drilldown`, requestConfig);
  }

  // eslint-disable-next-line default-param-last
  getSummary(since, until, type = 'account', id, groupBy, businessHours) {
    return axios.get(`${this.url}/summary`, {
      params: {
        since,
        until,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getConversationMetric(type = 'account', page = 1) {
    return axios.get(`${this.url}/conversations`, {
      params: {
        type,
        page,
      },
    });
  }

  getAgentReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/agents`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getConversationsSummaryReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/conversations_summary`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getConversationTrafficCSV({ daysBefore = 6 } = {}) {
    return axios.get(`${this.url}/conversation_traffic`, {
      params: { timezone_offset: getTimeOffset(), days_before: daysBefore },
    });
  }

  getLabelReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/labels`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getInboxReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/inboxes`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getTeamReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/teams`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getBotMetrics({ from, to, bot_type } = {}) {
    return axios.get(`${this.url}/bot_metrics`, {
      params: { since: from, until: to, bot_type },
    });
  }

  getBotSummary({ from, to, groupBy, businessHours, bot_type } = {}) {
    return axios.get(`${this.url}/bot_summary`, {
      params: {
        since: from,
        until: to,
        type: 'account',
        group_by: groupBy,
        business_hours: businessHours,
        bot_type,
      },
    });
  }

  askLumera({
    question,
    since,
    until,
    criterion,
    comparison,
    comparisonAgentId,
    goal,
  }) {
    return axios.post(`${this.url}/ask_lumera`, {
      question,
      since,
      until,
      criterion,
      comparison,
      comparison_agent_id: comparisonAgentId,
      goal,
      timezone_offset: getTimeOffset(),
    });
  }

  getLumeraInsightStatus(id) {
    return axios.get(`${this.url}/lumera_insight_status`, { params: { id } });
  }

  getLumeraAnalyticsReportQuota() {
    return axios.get(`${this.url}/lumera_analytics_report_quota`);
  }

  exportLumera({ format, analysis }) {
    return axios.post(`${this.url}/export_lumera`, {
      export_format: format,
      analysis,
    });
  }

  getLumeraExportStatus(exportId) {
    return axios.get(`${this.url}/lumera_export_status`, {
      params: { id: exportId },
    });
  }

  downloadLumeraExport(exportId) {
    return axios.get(`${this.url}/download_lumera_export`, {
      params: { id: exportId },
      responseType: 'blob',
    });
  }

  getSavedLumeraReports() {
    return axios.get(`${this.url}/saved_lumera_reports`);
  }

  saveLumeraReport({ name, question, filters, layout }) {
    return axios.post(`${this.url}/save_lumera_report`, {
      name,
      question,
      filters,
      layout,
    });
  }

  scheduleLumeraReport({ reportId, recipientEmail, frequency }) {
    return axios.post(`${this.url}/schedule_lumera_report`, {
      report_id: reportId,
      recipient_email: recipientEmail,
      frequency,
    });
  }

  getLumeraReportSchedules() {
    return axios.get(`${this.url}/lumera_report_schedules`);
  }

  updateLumeraReportSchedule(id, attributes) {
    return axios.patch(`${this.url}/update_lumera_report_schedule`, {
      id,
      ...attributes,
    });
  }

  deleteLumeraReportSchedule(id) {
    return axios.delete(`${this.url}/destroy_lumera_report_schedule`, {
      params: { id },
    });
  }

  deleteLumeraReport(id) {
    return axios.delete(`${this.url}/destroy_lumera_report`, {
      params: { id },
    });
  }
}

export default new ReportsAPI();
